import { useEffect, useState } from 'react'
import { format, startOfMonth, subMonths } from 'date-fns'
import type { DailyReportMetric } from 'api/generated'
import { Button } from 'components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table'
import {
  axisProps,
  changePct,
  formatMetric,
  formatRowDate,
} from 'helpers/daily-report'
import { cn } from 'lib/utils'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { fetchRevenueMonths } from 'store/dailyReport/dailyReportSlice'
import { useAppDispatch, useAppSelector } from 'store/store'

const MONTH_OPTIONS = [12, 24, 36]
const PREVIOUS_COLOR = 'var(--muted-foreground)'

type MonthPoint = {
  date: string
  value: number | null
  previous: number | null
  change: number | null
  // The month in progress: its data is partial
  current: boolean
}

const pct = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 })
const signedPct = (value: number | null) =>
  value == null ? '–' : `${value > 0 ? '+' : ''}${pct.format(value)}%`

const sum = (values: (number | null)[]) =>
  values.reduce<number>((total, v) => total + (v ?? 0), 0)

const MonthTooltip = ({
  active,
  payload,
  metric,
  compare,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
  metric: DailyReportMetric
  compare: boolean
}) => {
  const point = payload?.[0]?.payload as MonthPoint | undefined
  if (!active || !point) return null
  return (
    <div className='rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm'>
      <div className='text-muted-foreground'>
        {formatRowDate(point.date, 'month')}
        {point.current && ' (mese in corso, parziale)'}
      </div>
      <div className='font-semibold tabular-nums'>
        {formatMetric(metric, point.value)}
      </div>
      {compare && (
        <div className='text-muted-foreground tabular-nums'>
          Anno prima: {formatMetric(metric, point.previous)} (
          {signedPct(point.change)})
        </div>
      )}
    </div>
  )
}

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className='inline-flex items-center gap-1'>
    <span className='h-2 w-2 rounded-sm' style={{ background: color }} />
    {label}
  </span>
)

/**
 * Recharge revenue by month over the last 12/24/36 months, each month compared
 * with the same month of the previous year.
 */
export const RevenueByMonth = () => {
  const dispatch = useAppDispatch()
  const report = useAppSelector((state) => state.dailyReport.revenueMonths)
  const [months, setMonths] = useState(12)
  const [compare, setCompare] = useState(true)
  const [view, setView] = useState<'chart' | 'table'>('chart')

  useEffect(() => {
    dispatch(fetchRevenueMonths(months))
  }, [dispatch, months])

  const revenue = report?.metrics.find((m) => m.key === 'revenue')
  if (!report || !revenue) return null

  // Monthly rows are dated on the first day of the month
  const byMonth = new Map(
    report.rows.map((row) => [row.date.slice(0, 7), row.values.revenue ?? null])
  )
  const today = new Date()
  const points: MonthPoint[] = Array.from({ length: months }, (_, i) => {
    const month = startOfMonth(subMonths(today, months - 1 - i))
    const value = byMonth.get(format(month, 'yyyy-MM')) ?? null
    const previous =
      byMonth.get(format(subMonths(month, 12), 'yyyy-MM')) ?? null
    return {
      date: format(month, 'yyyy-MM-dd'),
      value,
      previous,
      change: changePct(value, previous),
      current: i === months - 1,
    }
  })

  const total = sum(points.map((p) => p.value))
  const firstWithData = points.find((p) => p.value != null)
  // Year on year only on closed months that have both values: the month in
  // progress against a whole month would always look like a drop
  const comparable = points.filter(
    (p) => !p.current && p.value != null && p.previous != null
  )
  const totalChange = changePct(
    sum(comparable.map((p) => p.value)),
    sum(comparable.map((p) => p.previous))
  )

  return (
    <div className='rounded-lg border p-4'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='space-y-2'>
          <div>
            <h3 className='text-sm font-semibold'>Ricavi per mese</h3>
            <p className='text-xs text-muted-foreground'>
              Ultimi {months} mesi, l&apos;ultimo è il mese in corso
              {compare && ', confrontati con lo stesso mese dell’anno prima'}
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-1'>
            {MONTH_OPTIONS.map((option) => (
              <Button
                key={option}
                size='sm'
                className='h-7 px-2 text-xs'
                variant={months === option ? 'secondary' : 'ghost'}
                onClick={() => setMonths(option)}
              >
                {option} mesi
              </Button>
            ))}
            <span className='mx-1 h-4 border-l' />
            <Button
              size='sm'
              className='h-7 px-2 text-xs'
              variant={compare ? 'secondary' : 'ghost'}
              aria-pressed={compare}
              onClick={() => setCompare(!compare)}
            >
              Confronta con l&apos;anno prima
            </Button>
            <Button
              size='sm'
              className='h-7 px-2 text-xs'
              variant='ghost'
              onClick={() => setView(view === 'chart' ? 'table' : 'chart')}
            >
              {view === 'chart' ? 'Tabella' : 'Grafico'}
            </Button>
          </div>
        </div>
        <div className='text-right'>
          <div className='text-xs text-muted-foreground'>
            Ricavi ultimi {months} mesi
            {firstWithData &&
              firstWithData !== points[0] &&
              ` (dati dal ${formatRowDate(firstWithData.date, 'month')})`}
          </div>
          <div className='text-2xl font-semibold tabular-nums'>
            {formatMetric(revenue, total)}
          </div>
          <div className='text-xs text-muted-foreground tabular-nums'>
            {totalChange == null
              ? 'Nessun dato per l’anno prima'
              : `${signedPct(totalChange)} rispetto all’anno prima` +
                ` (${comparable.length} mesi chiusi)`}
          </div>
        </div>
      </div>

      {view === 'chart' ? (
        <>
          {compare && (
            <div className='mt-3 flex gap-3 text-xs text-muted-foreground'>
              <LegendDot color='var(--primary)' label='Ricavi' />
              <LegendDot
                color={PREVIOUS_COLOR}
                label='Stesso mese anno prima'
              />
            </div>
          )}
          <div className='mt-3 h-[240px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={points}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                barGap={2}
              >
                <CartesianGrid
                  vertical={false}
                  stroke='var(--border)'
                  strokeDasharray='3 3'
                />
                <XAxis
                  dataKey='date'
                  tickFormatter={(d: string) => formatRowDate(d, 'month')}
                  minTickGap={24}
                  {...axisProps}
                />
                <YAxis
                  tickFormatter={(v: number) => formatMetric(revenue, v)}
                  width={80}
                  domain={[0, 'auto']}
                  {...axisProps}
                />
                <Tooltip
                  cursor={{ fill: 'var(--muted)' }}
                  content={({ active, payload }) => (
                    <MonthTooltip
                      active={active}
                      payload={payload}
                      metric={revenue}
                      compare={compare}
                    />
                  )}
                />
                {compare && (
                  <Bar
                    dataKey='previous'
                    fill={PREVIOUS_COLOR}
                    fillOpacity={0.35}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    isAnimationActive={false}
                  />
                )}
                <Bar
                  dataKey='value'
                  fill='var(--primary)'
                  radius={[4, 4, 0, 0]}
                  maxBarSize={compare ? 24 : 32}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className='mt-3 max-h-[320px] overflow-y-auto'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mese</TableHead>
                <TableHead className='text-right'>Ricavi</TableHead>
                <TableHead className='text-right'>Anno prima</TableHead>
                <TableHead className='text-right'>Variazione</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...points].reverse().map((point) => (
                <TableRow key={point.date}>
                  <TableCell>
                    {formatRowDate(point.date, 'month')}
                    {point.current && (
                      <span className='ms-1 text-xs text-muted-foreground'>
                        (parziale)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className='text-right tabular-nums'>
                    {formatMetric(revenue, point.value)}
                  </TableCell>
                  <TableCell className='text-right text-muted-foreground tabular-nums'>
                    {formatMetric(revenue, point.previous)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums',
                      point.current && 'text-muted-foreground'
                    )}
                  >
                    {signedPct(point.change)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
